<template>
  <RevTable
    ref="table-ref"
    class="bg-float-default-low shadow-short rounded-lg"
    :collection="expandableListings"
    :columns="tableColumns"
    data-qa="listings-table"
    expand-label=""
    :expandRowsByDefault="areAllListingsExpanded"
    :shouldMountExpandedRows="true"
    transparent-header
    @update:expanded-row="handleExpandedRowUpdate"
  >
    <template #body-image="{ item }">
      <RevIllustration
        id="image"
        :alt="item.title"
        class="min-h-[48px] min-w-[48px]"
        :height="48"
        :src="item.thumbnail.link?.href"
        :width="48"
      />
    </template>
    <template #body-product="{ item }">
      <ProductInfo
        :listing="item"
        :publication-state
        @refresh-listings="emit('refresh-listings')"
      />
    </template>
    <template #body-inventory="{ item }">
      <Inventory id="inventory" :listing="item" />
    </template>
    <template #body-markets="{ item }">
      <MarketList :markets="item.markets" />
    </template>
    <template #body-competition="{ item }">
      <CompetitionTag
        id="competition"
        :competition-level="item.competitionLevel"
      />
    </template>
    <template
      v-for="listing in listings"
      :key="listing.id"
      #[`expand-${listing.id}`]="{ item }"
    >
      <ExpandedListing :listing="item" />
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { computed, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'
import { RevTable } from '@ds/components/Table'
import type { Column } from '@ds/components/Table/Table.constant'

import type {
  GetListingsV2Response,
  PublicationState,
} from '~/api/listings/types'

import { useListingsTracking } from '../../composables/useListingsTracking'

import translations from './ListingsTable.translations'
import CompetitionTag from './components/CompetitionTag/CompetitionTag.vue'
import ExpandedListing from './components/ExpandedListing/ExpandedListing.vue'
import Inventory from './components/Inventory/Inventory.vue'
import MarketList from './components/MarketList/MarketList.vue'
import ProductInfo from './components/ProductInfo/ProductInfo.vue'

const tracking = useTracking()
const { formatListingToTrackingEvent } = useListingsTracking()

const props = defineProps<{
  listings: GetListingsV2Response['results']
  publicationState: PublicationState
  areAllListingsExpanded: boolean
}>()

const emit = defineEmits(['refresh-listings'])

const i18n = useI18n()

const tableRef = useTemplateRef('table-ref')

watch(
  () => props.areAllListingsExpanded,
  (value) => {
    if (value) {
      tableRef.value?.expandAll()
    } else {
      tableRef.value?.collapseAll()
    }
  },
)

const expandableListings = computed(() =>
  props.listings.map((item) => ({
    ...item,
    expandable: true,
  })),
)

const handleExpandedRowUpdate = (rowId: string, expanded: boolean) => {
  if (expanded) {
    tracking.trackClick({
      page_type: 'listings',
      zone: 'listing_details',
      name: 'open_details',
    })

    const listing = props.listings.find((item) => item.id === rowId)

    if (listing) {
      tracking.trackBoSellerListingImpression({
        page_type: 'listings',
        zone: 'listing_details',
        name: 'listing_impression',
        ...formatListingToTrackingEvent(listing),
      })
    }
  }
}

const tableColumns: Column[] = [
  { key: 'image', label: '' },
  {
    key: 'product',
    label: i18n(translations.product),
  },
  {
    key: 'inventory',
    label: i18n(translations.inventory),
  },
  {
    key: 'markets',
    label: i18n(translations.markets),
  },
  {
    key: 'competition',
    label: i18n(translations.competition),
  },
]
</script>

<style scoped>
/* Hide the column names */
:deep(td.group):before {
  @apply max-md:hidden;
}
/* Shift our row content to the left */
:deep(td.group) {
  @apply max-md:justify-start;
}
/* Remove RevTable's default text-align that occurs for mobile viewport */
:deep(td.group) > div {
  @apply max-md:text-start;
}
/* Hide the border bottom of our image column (which has no column name) */
:deep(td.group:has(#image)) {
  @apply max-md:border-b-0;
}

/* The inventory and competition column names should be visible */
:deep(td.group:has(#inventory)):before,
:deep(td.group:has(#competition)):before {
  @apply max-md:block;
}
:deep(td.group:has(#inventory)),
:deep(td.group:has(#competition)) {
  @apply max-md:justify-between;
}

/* The expand column's (which also has no name) content should be aligned to the right   */
:deep(td)[headers=''].group:last-of-type {
  @apply max-md:justify-end;
}

/* Our nested table (see ExpandedListing.vue) is within this row, 
  unfortunately it doesn't fill the row unless 
  the "display: block" is removed. */
:deep(tr[id*='expand']) {
  @apply max-md:table-row;
}
</style>
